import { useEffect, useState, useRef, useCallback } from 'react';

import './tour.css';

import { useMapData } from '../../map-data/use-map-data';
import { useNavigationBarData } from '../NavigationBar/NavigationBarProvider';
import type { MapExperience } from '../NavigationBar/NavigationBarProvider';
import { theme } from '../../styles/theme';

import {
  getHasSeenWelcome,
  setHasSeenWelcome,
  getHasSeenPoiTour,
  getHasSeenHeatmapTour,
  setLastView,
} from './virtual-tour-local-storage';
import { useVirtualTourConfig } from './VirtualTourProvider';
import { poiTour, heatmapTour, extendedVirtualTour } from './shepherd-tours';

/**
 * useVirtualTour
 *
 * Instantiates Shepherd virtual tour, if not already completed, and extended tour dependant if also
 * there are updates.
 */
export const useVirtualTour = () => {
  const { loading: mapDataLoading } = useMapData();
  const { poiTourReady, heatmapTourReady, hasUpdates, refreshExtendedTour } =
    useVirtualTourConfig();
  const { selectedExperience, setSelectedExperience } = useNavigationBarData();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const previousViewRef = useRef<MapExperience | undefined>(undefined);
  const hasShownExtendedTourRef = useRef(false);

  // Show welcome modal on first visit
  useEffect(() => {
    if (!mapDataLoading && !getHasSeenWelcome()) {
      setShowWelcomeModal(true);
    }
  }, [mapDataLoading]);

  // Auto-show extended tour for returning users with new updates
  useEffect(() => {
    if (mapDataLoading) return;
    if (!getHasSeenWelcome()) return; // New user - don't show extended tour
    if (!hasUpdates) return; // No updates to show
    if (hasShownExtendedTourRef.current) return; // Already shown this session

    // Don't show on smaller screens
    const isDesktopView = window.innerWidth >= theme.breakPoints.desktop;
    if (!isDesktopView) return;

    // Wait for tours to be ready before showing
    if (!poiTourReady && !heatmapTourReady) return;

    // Don't interrupt if a tour is already active
    if (
      poiTour.isActive() ||
      heatmapTour.isActive() ||
      extendedVirtualTour.isActive()
    )
      return;

    // Auto-show extended tour for returning users
    hasShownExtendedTourRef.current = true;
    refreshExtendedTour();
    extendedVirtualTour.start();
  }, [
    mapDataLoading,
    hasUpdates,
    poiTourReady,
    heatmapTourReady,
    refreshExtendedTour,
  ]);

  const handleWelcomeSelection = useCallback(
    (view: MapExperience) => {
      setHasSeenWelcome(true);
      setShowWelcomeModal(false);
      setSelectedExperience(view);
      setLastView(view === 'heatMap' ? 'heatmap' : 'poi');
    },
    [setSelectedExperience],
  );

  // Auto-start tours on first visit to each view and track view changes
  useEffect(() => {
    if (mapDataLoading) return;

    const hasSeenWelcome = getHasSeenWelcome();
    if (!hasSeenWelcome) return;

    // Don't start tours on smaller screens where the map is hidden
    const isDesktopView = window.innerWidth >= theme.breakPoints.desktop;
    if (!isDesktopView) return;

    // Detect view change
    const currentView = selectedExperience;
    const previousView = previousViewRef.current;

    if (currentView === 'pinMap') {
      setLastView('poi');
      // Auto-start POI tour if first visit (and not already shown for this view)
      if (
        previousView !== 'pinMap' &&
        !getHasSeenPoiTour() &&
        poiTourReady &&
        !poiTour.isActive() &&
        !extendedVirtualTour.isActive()
      ) {
        previousViewRef.current = currentView;
        poiTour.start();
      } else if (
        previousView !== 'pinMap' &&
        (poiTourReady || getHasSeenPoiTour())
      ) {
        // Update ref if tour didn't start because already seen
        // Don't update if tour isn't ready yet (to allow retry when config loads)
        previousViewRef.current = currentView;
      }
    }

    if (currentView === 'heatMap') {
      setLastView('heatmap');
      // Auto-start Heatmap tour if first visit (and not already shown for this view)
      if (
        previousView !== 'heatMap' &&
        !getHasSeenHeatmapTour() &&
        heatmapTourReady &&
        !heatmapTour.isActive() &&
        !extendedVirtualTour.isActive()
      ) {
        previousViewRef.current = currentView;
        heatmapTour.start();
      } else if (
        previousView !== 'heatMap' &&
        (heatmapTourReady || getHasSeenHeatmapTour())
      ) {
        // Update ref if tour didn't start because already seen
        // Don't update if tour isn't ready yet (to allow retry when config loads)
        previousViewRef.current = currentView;
      }
    }
  }, [mapDataLoading, selectedExperience, poiTourReady, heatmapTourReady]);

  return { showWelcomeModal, handleWelcomeSelection };
};
